/** SUNTRONIX ONE — Service Master (Commercial / Technology).
 *
 *  Legacy-imported services keep their provenance for a
 *  reconciliation preview.  Services created here are immediately
 *  usable inside the New Quotation composer.
 */
import { useEffect, useState, useCallback } from "react";
import { sourceLabel } from "@/lib/legacySource";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, Plus, Search, RefreshCw, X, ChevronRight, FileCheck2,
} from "lucide-react";
import { AdminChip } from "@/components/app/AdminMode";
import { MasterActions } from "@/components/app/MasterActions";

const DELIVERY_MODES = ["", "REMOTE", "ONSITE", "HYBRID", "COURIER", "IN_HOUSE"];
const UOMS = ["UNIT", "HOUR", "DAY", "MONTH", "PROJECT", "DEVICE",
                            "USER", "SITE", "INCIDENT"];

export default function ServiceMaster() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (category) params.category = category;
      const { data } = await api.get("/service-master", { params });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [category, q]);

  useEffect(() => {
    load();
    api.get("/service-master/categories").then(({ data }) =>
        setCategories(data.items || []));
  }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="service-master">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
            <Wrench className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold text-slate-900">
              Service Master
            </div>
            <div className="text-[11px] text-slate-500">
              Commercial · Technology — approved services available for quotations
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AdminChip area="service" label="Configure service fields" testid="admin-chip-service" />
          <AdminChip area="picklists" pl="service_category" label="Edit categories" testid="admin-chip-service-cats" />
          <button onClick={() => setShowNew(true)}
              data-testid="service-new"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add service
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[420px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search service name, code, SAC, description"
              data-testid="service-search"
              className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-slate-200 rounded-md focus:outline-none focus:border-slate-400" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="service-filter-category"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All categories</option>
          {categories.map((c) =>
            <option key={c.key || c.id} value={c.label}>{c.label}</option>)}
        </select>
        <button onClick={load}
            className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">SAC</th>
              <th className="text-right px-3 py-2">Rate</th>
              <th className="text-left px-3 py-2">UoM</th>
              <th className="text-left px-3 py-2">GST %</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={9} className="text-center py-6 text-slate-400">
                No services yet. Click <b>Add service</b> to create one.
              </td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`service-row-${s.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{s.service_name}</div>
                  {s.description && (
                    <div className="text-[10px] text-slate-500 truncate max-w-[420px]">{s.description}</div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{s.service_code || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{s.category || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{s.sac_code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{(s.standard_selling_price || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-slate-600">{s.unit_of_measure || "UNIT"}</td>
                <td className="px-3 py-2 text-slate-600">{s.gst_percent || 0}%</td>
                <td className="px-3 py-2">
                  <span className={"text-[10px] px-1.5 py-0.5 rounded ring-1 " +
                      (/vtiger/i.test(s.source || "")
                          ? "bg-violet-50 text-violet-700 ring-violet-200"
                          : "bg-slate-100 text-slate-600 ring-slate-200")}>
                    {sourceLabel(s.source) || "MANUAL"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelected(s.id)}
                      data-testid={`service-open-${s.id}`}
                      className="text-slate-500 hover:text-slate-900 text-[11px]">
                    Service 360 <ChevronRight className="h-3 w-3 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewServiceModal categories={categories}
                                        onClose={() => setShowNew(false)}
                                        onCreated={() => { setShowNew(false); load(); }} />}
      {selected && <Service360Drawer serviceId={selected}
                                        onClose={() => setSelected(null)} onChange={load} />}
      
    </div>
  );
}

/* ---------- NEW SERVICE MODAL --------------------------------------------- */
export function NewServiceModal({ categories, onClose, onCreated, presetName }) {
  const [form, setForm] = useState({
    service_name: presetName || "", service_code: "", category: "",
    subcategory: "", description: "", sac_code: "", gst_percent: 18,
    standard_selling_price: 0, currency: "INR", unit_of_measure: "UNIT",
    delivery_mode: "", estimated_duration: "", status: "ACTIVE",
    source: "MANUAL", country_availability: [],
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.service_name.trim()) return toast.error("Service name required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.delivery_mode) delete payload.delivery_mode;
      const { data } = await api.post("/service-master", payload);
      toast.success(`Service created: ${data.service_name}`);
      onCreated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4"
        data-testid="service-new-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900">Add service</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 text-[12px] flex-1 overflow-y-auto">
          <Field label="Service name *" full>
            <input value={form.service_name} onChange={(e) => setForm({...form, service_name: e.target.value})}
                data-testid="svc-name"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Service code">
            <input value={form.service_code} onChange={(e) => setForm({...form, service_code: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              <option value="">Select category…</option>
              {categories.map((c) => <option key={c.key || c.id} value={c.label}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="SAC code">
            <input value={form.sac_code} onChange={(e) => setForm({...form, sac_code: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </Field>
          <Field label="GST %">
            <input type="number" value={form.gst_percent} onChange={(e) => setForm({...form, gst_percent: parseFloat(e.target.value) || 0})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Standard selling price (INR)">
            <input type="number" value={form.standard_selling_price} onChange={(e) => setForm({...form, standard_selling_price: parseFloat(e.target.value) || 0})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Unit of measure">
            <select value={form.unit_of_measure} onChange={(e) => setForm({...form, unit_of_measure: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Delivery mode">
            <select value={form.delivery_mode} onChange={(e) => setForm({...form, delivery_mode: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {DELIVERY_MODES.map((m) => <option key={m || "any"} value={m}>{m || "—"}</option>)}
            </select>
          </Field>
          <Field label="Estimated duration">
            <input value={form.estimated_duration} onChange={(e) => setForm({...form, estimated_duration: e.target.value})}
                placeholder="e.g. 2 hours per device"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Description" full>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3} data-testid="svc-desc"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy}
              data-testid="svc-submit"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create service"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ---------- SERVICE 360 DRAWER -------------------------------------------- */
function Service360Drawer({ serviceId, onClose, onChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/service-master/${serviceId}/service-360`).then(({ data }) => setData(data))
        .catch(() => toast.error("Load failed"));
  }, [serviceId]);
  const s = data?.service;
  if (!s) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end"
        data-testid="service-360-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-heading font-semibold text-slate-900">{s.service_name}</div>
            <div className="text-[10px] text-slate-500">Service 360 · {s.service_code || "—"}</div>
          </div>
          <div className="flex items-center gap-2">
            <MasterActions type="service" id={serviceId} name={s.service_name}
              active={s.status === "ACTIVE"} testid="service-360"
              onChanged={(res) => { onChange?.(); if (res?.deleted) onClose(); else api.get(`/service-master/${serviceId}/service-360`).then((r) => setData(r.data)).catch(() => {}); }} />
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <Section title="Commercial">
            <Row label="Category" value={s.category} />
            <Row label="SAC" value={s.sac_code} mono />
            <Row label="GST %" value={`${s.gst_percent || 0}%`} />
            <Row label="Standard price" value={`₹${(s.standard_selling_price || 0).toLocaleString("en-IN")}`} />
            <Row label="Unit of measure" value={s.unit_of_measure} />
            <Row label="Delivery mode" value={s.delivery_mode || "—"} />
            <Row label="Currency" value={s.currency} />
            <Row label="Status" value={s.status} />
          </Section>
          <Section title="Description">
            <div className="text-[12px] text-slate-700 whitespace-pre-wrap">
              {s.description || "—"}
            </div>
          </Section>
          <Section title={`Related quotations · ${data.related_quotations.length}`}>
            {data.related_quotations.length === 0
                ? <div className="text-[11px] text-slate-400">None yet.</div>
                : data.related_quotations.slice(0, 8).map((q) => (
                    <div key={q.id} className="text-[11px] flex items-center justify-between py-1 border-b border-slate-100">
                      <div className="truncate">
                        <b>{q.code}</b> · {q.subject}
                        <span className="text-slate-400"> · {q.customer_name}</span>
                      </div>
                      <span className="text-slate-500">{q.status}</span>
                    </div>
                  ))}
          </Section>
          <Section title="History">
            <Row label="Source" value={s.source} />
            <Row label="Legacy import ID" value={s.vtiger_service_id || "—"} mono />
            <Row label="Created" value={s.created_at?.slice(0, 19).replace("T", " ")} />
            <Row label="Updated" value={s.updated_at?.slice(0, 19).replace("T", " ")} />
            <Row label="Last synced" value={s.last_synced_at?.slice(0, 19).replace("T", " ") || "—"} />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <div className="text-slate-500">{label}</div>
      <div className={"text-slate-900 truncate " + (mono ? "font-mono" : "")}>{value || "—"}</div>
    </div>
  );
}

